import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import React from 'react';
import { pdf, Page, Text, View, StyleSheet, Link, Document } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';

// ============================================================================
// ORIGINAL UTILS FROM V1
// ============================================================================
export function cn(...inputs) {
	return twMerge(clsx(inputs));
}

// Optira backend client for the Format-Resume frontend.
// Keeps UI unchanged; only wires backend calls.

export function getApiBaseUrl() {
  return `/api`;
}

function getSessionToken() {
  return (
    localStorage.getItem('fr_session_token') ||
    sessionStorage.getItem('fr_session_token') ||
    ''
  );
}

export async function getGuestStatus() {
  const res = await fetch(`${getApiBaseUrl()}/guest_status/`, {
    method: 'GET',
    credentials: 'include',
  });
  const data = await safeJson(res);
  if (!res.ok) {
    throw new Error(data?.error || data?.message || 'Failed to fetch guest status');
  }
  return data;
}

async function safeJson(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

function normalizeFileAndOpts(fileOrPayload, opts = {}) {
  // Backward/defensive support: allow either (file, opts) or ({file, username...})
  if (
    fileOrPayload &&
    typeof fileOrPayload === 'object' &&
    !(fileOrPayload instanceof Blob) &&
    'file' in fileOrPayload
  ) {
    const { file, ...rest } = fileOrPayload;
    return { file, opts: { ...rest, ...opts } };
  }
  return { file: fileOrPayload, opts };
}

export async function uploadResumeToOptira(fileOrPayload, opts = {}) {
  const { file, opts: finalOpts } = normalizeFileAndOpts(fileOrPayload, opts);
  const { username, useremail, tag } = finalOpts || {};
  if (!(file instanceof Blob)) {
    throw new TypeError('uploadResumeToOptira: file must be a File/Blob');
  }
  const fd = new FormData();
  fd.append('username', username || 'testuser');
  fd.append('useremail', useremail || 'optira@thirdeyedata.ai');
  fd.append('tag', tag || 'test');
  // backend expects "files" (can be list). We'll send one.
  // Provide filename explicitly for better cross-browser compatibility.
  fd.append('files', file, (file && file.name) ? file.name : 'resume.pdf');

  const res = await fetch(`${getApiBaseUrl()}/upload_multiple_resume/`, {
    method: 'POST',
    body: fd,
    credentials: 'include',
    headers: getSessionToken() ? { Authorization: `Bearer ${getSessionToken()}` } : {},
  });
  const data = await safeJson(res);
  if (!res.ok) {
    throw new Error(data?.error || data?.message || 'Upload failed');
  }
  return data;
}

export async function processResumeWithOptira(fileOrPayload, opts = {}) {
  const { file, opts: finalOpts } = normalizeFileAndOpts(fileOrPayload, opts);
  const { formatType = 'thirdeye' } = finalOpts || {};
  if (!(file instanceof Blob)) {
    throw new TypeError('processResumeWithOptira: file must be a File/Blob');
  }
  const fd = new FormData();
  fd.append('formatType', formatType);
  // backend expects the uploaded file under key "files" (single)
  fd.append('files', file);

  const res = await fetch(`${getApiBaseUrl()}/process_resume/`, {
    method: 'POST',
    body: fd,
    credentials: 'include',
    headers: getSessionToken() ? { Authorization: `Bearer ${getSessionToken()}` } : {},
  });
  const data = await safeJson(res);
  if (!res.ok) {
    throw new Error(data?.error || data?.message || 'Resume processing failed');
  }
  return data;
}

export async function uploadAndProcessResume(fileOrPayload, opts = {}) {
  const { file } = normalizeFileAndOpts(fileOrPayload, opts);
  if (!(file instanceof Blob)) {
    throw new TypeError('uploadAndProcessResume: file must be a File/Blob');
  }
  const fd = new FormData();
  fd.append('files', file);

  const res = await fetch(`${getApiBaseUrl()}/upload_and_process_resume/`, {
    method: 'POST',
    body: fd,
    credentials: 'include',
    headers: getSessionToken() ? { Authorization: `Bearer ${getSessionToken()}` } : {},
  });
  const data = await safeJson(res);
  if (!res.ok) {
    throw new Error(data?.error || data?.message || data?.detail || 'Resume processing failed');
  }
  return data;
}

export async function sendResumeEmail({ name, companyName, email, template, resumeName, pdfBlob, filename }) {
  const formData = new FormData();
  formData.append('name', (name || '').trim());
  formData.append('company_name', (companyName || '').trim());
  formData.append('email', (email || '').trim());
  formData.append('template', template || 'modern');

  const safeFilename = (filename || resumeName || 'resume').trim() || 'resume';
  const pdfFile = pdfBlob instanceof Blob ? pdfBlob : new Blob([], { type: 'application/pdf' });
  formData.append('pdf_file', pdfFile, safeFilename.toLowerCase().endsWith('.pdf') ? safeFilename : `${safeFilename}.pdf`);

  const res = await fetch(`${getApiBaseUrl()}/send_resume_via_email/`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  const data = await safeJson(res);
  if (!res.ok) {
    throw new Error(data?.detail || data?.message || 'Failed to send resume email.');
  }

  return data;
}
export async function convertHtmlToPdf(html, { filename = 'resume.pdf' } = {}) {
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}/convert_resume_pdf/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ html, filename }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `convert_resume_pdf failed (${res.status})`);
  }
  const blob = await res.blob();
  return blob;
}

export async function convertHtmlToDocx(html, { filename = 'resume.docx' } = {}) {
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}/convert_resume_docx/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ html, filename }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `convert_resume_docx failed (${res.status})`);
  }
  const blob = await res.blob();
  return blob;
}

export async function convertResumeToDocx(resumeData, templateId, { filename = 'resume.docx' } = {}) {
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}/convert_resume_docx/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resumeData, templateId, filename }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `convert_resume_docx failed (${res.status})`);
  }
  return await res.blob();
}



// Improved Resume Model - Handles multiple JSON structures flexibly
// This file maps various parsed JSON formats into a unified resume model

function firstNonEmpty(...vals) {
  for (const v of vals) {
    if (v === null || v === undefined) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return '';
}

// Helper to safely extract array from various formats
function extractArray(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    // Check if it's an object with numbered keys or single key
    const values = Object.values(data);
    if (values.length > 0 && typeof values[0] === 'string') {
      return values;
    }
  }
  return [];
}

// Helper: check if a string looks like a job title (short, reasonable)
function looksLikeTitle(str) {
  if (!str) return false;
  const clean = str.trim();
  // Title should be under 50 chars and not contain sentence-ending punctuation
  if (clean.length > 50) return false;
  if (clean.includes('.') || clean.includes(',')) return false;
  // Check if it's just roman numerals or initials (JR, SR, III, etc)
  if (/^[IVXJRS.]+$/i.test(clean)) return false;
  return true;
}

// Parse education from multiple formats
function normalizeEducation(parsed) {
  const items = [];
  const eduData = parsed?.education || parsed?.Education;
  
  if (!eduData) return items;

  // Format 1: Array of objects (new format - Sagnik's resume)
  if (Array.isArray(eduData)) {
    return eduData.map(edu => ({
      degree: edu.degree || '',
      institution: edu.institution || edu.school || '',
      years: edu.date_range || edu.date || edu.end_date || edu.years || '',
      field: edu.field || '',
      location: edu.location || '',
      gpa: edu.gpa || edu.cgpa || edu.grade || edu.percentage || edu.score || ''
    }));
  }

  // Format 2: Nested object (old format)
  if (typeof eduData === 'object') {
    for (const [institution, degrees] of Object.entries(eduData)) {
      if (!degrees || typeof degrees !== 'object') continue;
      for (const [degree, datesObj] of Object.entries(degrees)) {
        const years = (datesObj && typeof datesObj === 'object') ? Object.keys(datesObj)[0] : '';
        items.push({ degree, institution, years, field: '', location: '' });
      }
    }
  }

  return items;
}

// Helper: ensure bullets is always a flat string array
function ensureBulletsArray(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(b => typeof b === 'string' && b.trim());
  if (typeof raw === 'string') {
    // Split multi-line strings into individual bullets
    const lines = raw.split(/\n/).map(l => l.trim()).filter(Boolean);
    return lines.length > 0 ? lines : [raw];
  }
  return [];
}

// Parse work experience from multiple formats
function normalizeExperience(parsed) {
  const items = [];
  const expData = parsed?.experience || parsed?.['Work Experience'];
  
  if (!expData) return items;

  // Format 1: Array of objects
  if (Array.isArray(expData)) {
    return expData.map(exp => ({
      title: exp.title || exp.role || '',
      company: exp.company || '',
      location: exp.location || '',
      dates: exp.date_range || exp.dates || '',
      bullets: ensureBulletsArray(exp.highlights || exp.bullets || exp.description || [])
    }));
  }

  // Format 2: Nested object (old format)
  if (typeof expData === 'object') {
    for (const [company, roles] of Object.entries(expData)) {
      if (!roles || typeof roles !== 'object') continue;
      for (const [role, datesObj] of Object.entries(roles)) {
        if (!datesObj || typeof datesObj !== 'object') continue;
        for (const [dates, detail] of Object.entries(datesObj)) {
          const raw = (detail && typeof detail === 'object') ? (detail[''] || detail.bullets || []) : [];
          items.push({ 
            title: role, 
            company, 
            dates, 
            location: '', 
            bullets: ensureBulletsArray(raw)
          });
        }
      }
    }
  }

  return items;
}

// Parse skills from multiple formats
function normalizeSkills(parsed) {
  const skills = [];
  const skillsData = parsed?.skills || parsed?.Skills;
  
  if (!skillsData) return skills;

  // Format 1: Array (strings or objects with name)
  if (Array.isArray(skillsData)) {
    return skillsData
      .map(s => {
        if (typeof s === 'string') return s.trim();
        if (s && typeof s === 'object' && s.name) return String(s.name).trim();
        return '';
      })
      .filter(Boolean);
  }

  // Format 2: Object with categories
  if (skillsData.categories && Array.isArray(skillsData.categories)) {
    skillsData.categories.forEach(cat => {
      if (cat.items && Array.isArray(cat.items)) {
        skills.push(...cat.items);
      }
    });
    return skills.filter(Boolean);
  }

  // Format 3: Object with array values
  if (typeof skillsData === 'object') {
    for (const v of Object.values(skillsData)) {
      if (Array.isArray(v)) skills.push(...v);
      else if (typeof v === 'string') skills.push(v);
    }
  }

  return skills.filter(Boolean);
}

// Helper: detect if a string looks like a sentence/description rather than a title
function looksLikeDescription(str) {
  if (!str) return false;
  const s = str.trim();
  if (s.length > 80) return true;
  // Contains sentence-ending punctuation mid/end
  if (/[.!]\s/.test(s) || /[.!]$/.test(s)) return true;
  return false;
}

// Helper: try to split "Title - Description" or "Title: Description" patterns
function splitProjectNameDesc(rawName) {
  if (!rawName) return { name: '', description: '' };
  const s = rawName.trim();

  // Pattern: "Project Name - Description text here"
  const dashMatch = s.match(/^(.{3,50?})\s+[-–—]\s+(.+)$/s);
  if (dashMatch && dashMatch[2].length > 20) {
    return { name: dashMatch[1].trim(), description: dashMatch[2].trim() };
  }

  // Pattern: "Project Name: Description text here"
  const colonMatch = s.match(/^(.{3,50?}):\s+(.+)$/s);
  if (colonMatch && colonMatch[2].length > 20) {
    return { name: colonMatch[1].trim(), description: colonMatch[2].trim() };
  }

  // If the whole thing looks like a description, treat it as such
  if (looksLikeDescription(s)) {
    return { name: '', description: s };
  }

  return { name: s, description: '' };
}

function projectDateRange(value) {
  if (!value) return '';
  const match = String(value).match(/(?:(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w*\.?\s+)?\d{4}\s*[-–—]\s*(?:(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w*\.?\s+)?(?:\d{4}|Present)/i);
  return match ? match[0] : '';
}

function isProjectContinuation(entry) {
  const name = String(entry?.name || '').trim();
  const bullets = Array.isArray(entry?.bullets) ? entry.bullets : [];
  const description = String(entry?.description || '').trim();
  const dates = String(entry?.dates || '').trim();
  if (!name) return false;
  if (dates) return false;
  if (projectDateRange(name) && name.replace(projectDateRange(name), '').trim().length === 0) return true;
  if (/^[a-z0-9(]/.test(name)) return true;
  if (name.length > 120 && bullets.length === 0 && !description) return true;
  if (/[.:]$/.test(name)) return true;
  if ((bullets.length > 0 || description) && name.split(/\s+/).length <= 12) return false;
  if (bullets.length === 0 && description && name.split(/\s+/).length > 12) return true;
  return false;
}

function mergeProjects(previous, current) {
  return {
    ...previous,
    dates: previous.dates || current.dates || '',
    bullets: [...(previous.bullets || []), ...(current.name ? [current.name] : []), ...(current.bullets || [])].filter(Boolean),
    links: [...new Set([...(previous.links || []), ...(current.links || [])])],
  };
}

// Parse projects from multiple formats
function normalizeProjects(parsed) {
  const projects = [];
  const projData = parsed?.projects || parsed?.Projects;
  
  if (!projData) return projects;

  // Format 1: Array of objects
  if (Array.isArray(projData)) {
    const normalized = projData.map(proj => {
      let rawName = proj.name || proj.title || proj.project || '';
      let rawDesc = Array.isArray(proj.description)
        ? proj.description.join(' ')
        : (proj.description || proj.summary || proj.details || '');
      let bullets = Array.isArray(proj.bullets)
        ? proj.bullets
        : (Array.isArray(proj.description) ? proj.description : []);
      const links = Array.isArray(proj.links) ? proj.links : (proj.links ? [proj.links] : []);
      let dates = proj.dates || proj.date || proj.duration || '';

      if (!rawDesc && rawName) {
        const split = splitProjectNameDesc(rawName);
        rawName = split.name;
        rawDesc = split.description;
      }

      if (!dates && rawName) {
        const extractedDates = projectDateRange(rawName);
        if (extractedDates) {
          dates = extractedDates;
          rawName = rawName.replace(extractedDates, '').replace(/[\s|,\-–—]+$/, '').trim();
        }
      }

      return {
        name: rawName,
        dates,
        description: rawDesc,
        bullets,
        links,
      };
    });

    return normalized.reduce((acc, project) => {
      const previous = acc[acc.length - 1];
      if (previous && isProjectContinuation(project)) {
        acc[acc.length - 1] = mergeProjects(previous, project);
        return acc;
      }
      acc.push(project);
      return acc;
    }, []);
  }

  // Format 2: Object with project names as keys
  if (typeof projData === 'object') {
    return Object.entries(projData).map(([name, description]) => ({
      name,
      dates: '',
      description: typeof description === 'string' ? description : '',
      bullets: [],
      links: []
    }));
  }

  return projects;
}

// Parse certifications
function normalizeCertifications(parsed) {
  const certs = [];
  const certData = parsed?.certifications || parsed?.Certifications;
  
  if (!certData) return certs;

  // Format 1: Array of objects
  if (Array.isArray(certData)) {
    return certData.map(cert => ({
      name: cert.name || cert.title || (typeof cert === 'string' ? cert : ''),
      issuer: cert.issuer || cert.organization || ''
    }));
  }

  // Format 2: Array of strings or object
  if (typeof certData === 'object') {
    return Object.values(certData).map(cert => ({
      name: typeof cert === 'string' ? cert : (cert.name || ''),
      issuer: cert.issuer || ''
    }));
  }

  return certs;
}

// Parse additional sections (for resumes like Sreeja's)
function normalizeAdditionalSections(parsed) {
  const sections = parsed?.additionalSections || parsed?.additional_sections || parsed?.['Additional Sections'] || [];

  if (Array.isArray(sections)) {
    return sections.map(section => ({
      heading: section.heading || section.title || '',
      content: section.content || section.items || []
    })).filter(s => s.heading);
  }

  return [];
}

// Main transformation function
export function optiraParsedToResumeModel(parsed, { fallbackEmail } = {}) {
  if (!parsed) return null;

  // Extract basics section (handles both formats)
  const basics = parsed?.basics || {};
  const contact = basics.contact || parsed?.contact || {};
  const location = basics.location || parsed?.location || {};

  const name = firstNonEmpty(
    basics.name, 
    parsed?.name, 
    parsed?.Name,
    ''
  );

  // CRITICAL FIX: Only use headline if it looks like a title (short, no punctuation)
  // Otherwise leave title empty rather than showing a description
  const headlineCandidate = basics.headline || parsed?.headline || '';
  const title = looksLikeTitle(headlineCandidate) 
    ? headlineCandidate 
    : firstNonEmpty(
        basics.title,
        parsed?.title,
        parsed?.Title,
        parsed?.Role,
        ''
      );

  const email = firstNonEmpty(
    contact.email,
    basics.email,
    parsed?.email,
    parsed?.Email,
    fallbackEmail,
    ''
  );

  const phone = firstNonEmpty(
    contact.phone,
    basics.phone,
    parsed?.phone,
    parsed?.Phone,
    ''
  );

  const locationStr = firstNonEmpty(
    location.city ? `${location.city}${location.country ? ', ' + location.country : ''}` : '',
    location.address,
    contact.location,
    parsed?.Location,
    parsed?.location,
    ''
  );

  // Extract links (LinkedIn, GitHub, etc.) - CRITICAL FIX
  const links = contact.links || basics.links || parsed?.links || [];
  const linkedin = links.find(l => l.label?.toLowerCase().includes('linkedin'))?.url || 
                   contact.linkedin || 
                   basics.linkedin ||
                   parsed?.linkedin || 
                   '';
  const github = links.find(l => l.label?.toLowerCase().includes('github'))?.url || 
                 contact.github || 
                 basics.github ||
                 parsed?.github || 
                 '';
  const website = contact.website || basics.website || parsed?.website || '';

  // Professional summary
  const professionalSummary = Array.isArray(basics.professional_summary) 
    ? basics.professional_summary.join(' ') 
    : (basics.professional_summary || basics.summary || parsed?.summary || parsed?.Summary || '');

  return {
    name,
    title,
    contact: {
      email,
      phone,
      location: locationStr,
      linkedin,
      github,
      website,
    },
    summary: professionalSummary,
    education: normalizeEducation(parsed),
    experience: normalizeExperience(parsed),
    skills: normalizeSkills(parsed),
    projects: normalizeProjects(parsed),
    certifications: normalizeCertifications(parsed),
    publications: (parsed?.publications || []).map(pub => ({
      title: pub.title || pub.name || '',
      venue: pub.venue || pub.journal || pub.conference || '',
      year: pub.year || pub.date || '',
      link: pub.link || pub.url || pub.doi || ''
    })).filter(p => p.title),
    interests: (parsed?.interests || parsed?.hobbies || []).map(i => typeof i === 'string' ? i : (i.name || '')).filter(Boolean),
    additionalSections: normalizeAdditionalSections(parsed),
  };
}

// Generate standalone HTML resume (used for the preview/export)
export function generateStandaloneHtml(resume, templateId = 'modern') {
  return buildDownloadHtml(templateId, resume);
}

export function buildDownloadHtml(templateId, resume) {
  const safe = (str) => String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const bullets = (arr) => (arr || [])
    .filter(Boolean)
    .map((b) => `<li>${safe(b)}</li>`)
    .join('');

  const chips = (arr) => (arr || [])
    .filter(Boolean)
    .map((x) => `<span class="chip">${safe(x)}</span>`)
    .join('');

  // A4 dimensions: 210mm × 297mm = 794px × 1123px at 96 DPI
  const baseCss = `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    
    body {
      margin: 0;
      padding: 0;
      font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
      color: #111827;
      font-size: 11pt;
      line-height: 1.4;
    }
    
    .page {
      width: 210mm;
      min-height: 297mm;
      padding: 20mm;
      background: #fff;
      margin: 0 auto;
    }
    
    h1, h2, h3, h4, h5, h6 { margin: 0; font-weight: 600; }
    h1 { font-size: 28pt; line-height: 1.2; }
    h2 { font-size: 14pt; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 8px; }
    h3 { font-size: 12pt; }
    
    p { margin: 4px 0; }
    
    ul {
      margin: 4px 0 8px 0;
      padding-left: 20px;
      list-style-type: disc;
    }
    
    li {
      margin-bottom: 3px;
      line-height: 1.4;
    }
    
    .header {
      border-bottom: 2px solid #e5e7eb;
      padding-bottom: 12px;
      margin-bottom: 16px;
    }
    
    .section {
      margin-top: 16px;
      page-break-inside: avoid;
    }
    
    .section-title {
      font-size: 13pt;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      margin-bottom: 10px;
      padding-bottom: 4px;
      border-bottom: 1.5px solid currentColor;
    }
    
    .muted { color: #6b7280; }
    
    .chip {
      display: inline-block;
      padding: 3px 8px;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      margin: 0 4px 4px 0;
      font-size: 9pt;
      background: #f9fafb;
    }
    
    .row {
      display: flex;
      gap: 20px;
      margin-top: 12px;
    }
    
    .col { flex: 1; }
    
    .experience-item,
    .education-item,
    .project-item {
      margin-bottom: 12px;
      page-break-inside: avoid;
    }
    
    .item-header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      margin-bottom: 4px;
    }
    
    .item-title { font-weight: 600; font-size: 11pt; }
    .item-subtitle { color: #4b5563; font-size: 10pt; }
    .item-date { color: #9ca3af; font-size: 9pt; white-space: nowrap; }
    
    @media print {
      body { margin: 0; }
      .page { margin: 0; box-shadow: none; }
    }
  `;

  const accentMap = {
    modern: '#4f46e5',     // Indigo
    professional: '#111827', // Dark gray
    creative: '#db2777',   // Pink
    minimal: '#111827',    // Dark gray
  };
  const accent = accentMap[templateId] || accentMap.modern;

  // Header section
  const headerHtml = `
    <div class="header">
      <h1 style="margin-bottom: 6px;">${safe(resume.name || 'Your Name')}</h1>
      ${resume.title ? `<div style="font-size: 14pt; color: ${accent}; font-weight: 600; margin-bottom: 8px;">${safe(resume.title)}</div>` : ''}
      <div style="font-size: 10pt; color: #6b7280;">
        ${resume.contact?.email ? `${safe(resume.contact.email)}` : ''}
        ${resume.contact?.phone && resume.contact?.email ? ' • ' : ''}
        ${resume.contact?.phone ? `${safe(resume.contact.phone)}` : ''}
        ${resume.contact?.location && (resume.contact?.email || resume.contact?.phone) ? ' • ' : ''}
        ${resume.contact?.location ? `${safe(resume.contact.location)}` : ''}
        ${resume.contact?.linkedin ? `<br><a href="${safe(resume.contact.linkedin)}" style="color: ${accent}; text-decoration: none;">LinkedIn</a>` : ''}
        ${resume.contact?.github ? ` • <a href="${safe(resume.contact.github)}" style="color: ${accent}; text-decoration: none;">GitHub</a>` : ''}
      </div>
    </div>
  `;

  // Summary section
  const summaryHtml = resume.summary ? `
    <div class="section">
      <h2 class="section-title" style="color: ${accent};">Professional Summary</h2>
      <p style="text-align: justify;">${safe(resume.summary)}</p>
    </div>
  ` : '';

  // Experience section
  const expHtml = (resume.experience && resume.experience.length > 0) ? `
    <div class="section">
      <h2 class="section-title" style="color: ${accent};">Work Experience</h2>
      ${resume.experience.map((e) => `
        <div class="experience-item">
          <div class="item-header">
            <div>
              <div class="item-title">${safe(e.title)}</div>
              <div class="item-subtitle" style="color: ${accent};">${safe(e.company)}${e.location ? ` • ${safe(e.location)}` : ''}</div>
            </div>
            <div class="item-date">${safe(e.dates)}</div>
          </div>
          ${e.bullets && e.bullets.length > 0 ? `<ul>${bullets(e.bullets)}</ul>` : ''}
        </div>
      `).join('')}
    </div>
  ` : '';

  // Education section
  const eduHtml = (resume.education && resume.education.length > 0) ? `
    <div class="section">
      <h2 class="section-title" style="color: ${accent};">Education</h2>
      ${resume.education.map((ed) => `
        <div class="education-item">
          <div class="item-header">
            <div>
              <div class="item-title">${safe(ed.degree)}${ed.field ? ` in ${safe(ed.field)}` : ''}</div>
              <div class="item-subtitle">${safe(ed.institution)}${ed.location ? ` • ${safe(ed.location)}` : ''}</div>
            </div>
            <div class="item-date">${safe(ed.years)}</div>
          </div>
        </div>
      `).join('')}
    </div>
  ` : '';

  // Skills section
  const skillsHtml = (resume.skills && resume.skills.length > 0) ? `
    <div class="section">
      <h2 class="section-title" style="color: ${accent};">Skills</h2>
      <div>${chips(resume.skills)}</div>
    </div>
  ` : '';

  // Projects section
  const projectsHtml = (resume.projects && resume.projects.length > 0) ? `
    <div class="section">
      <h2 class="section-title" style="color: ${accent};">Projects</h2>
      ${resume.projects.map((p) => `
        <div class="project-item">
          <div class="item-title">${safe(p.name)}</div>
          <p class="muted">${safe(Array.isArray(p.description) ? p.description.join(' ') : p.description)}</p>
          ${p.links && p.links.length > 0 ? `
            <div style="margin-top: 4px; font-size: 9pt;">
              ${p.links.map(link => `<a href="${safe(link.url)}" style="color: ${accent}; margin-right: 12px;">${safe(link.label)}</a>`).join('')}
            </div>
          ` : ''}
        </div>
      `).join('')}
    </div>
  ` : '';

  // Certifications section
  const certificationsHtml = (resume.certifications && resume.certifications.length > 0) ? `
    <div class="section">
      <h2 class="section-title" style="color: ${accent};">Certifications</h2>
      ${resume.certifications.map(cert => `
        <div style="margin-bottom: 6px;">
          <div style="font-weight: 600;">${safe(cert.name)}</div>
          ${cert.issuer ? `<div class="muted" style="font-size: 10pt;">${safe(cert.issuer)}</div>` : ''}
        </div>
      `).join('')}
    </div>
  ` : '';

  // Additional sections (for flexible content)
  const additionalHtml = (resume.additionalSections && resume.additionalSections.length > 0) ? 
    resume.additionalSections.map(section => `
      <div class="section">
        <h2 class="section-title" style="color: ${accent};">${safe(section.heading)}</h2>
        ${Array.isArray(section.content) ? `
          <ul>
            ${section.content.map(item => `<li>${safe(item)}</li>`).join('')}
          </ul>
        ` : `<p>${safe(section.content)}</p>`}
      </div>
    `).join('') : '';

  // Assemble the layout
  const body = `
    ${headerHtml}
    ${summaryHtml}
    ${expHtml}
    ${eduHtml}
    ${projectsHtml}
    ${skillsHtml}
    ${certificationsHtml}
    ${additionalHtml}
  `;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Resume - ${safe(resume.name)}</title>
  <style>${baseCss}</style>
</head>
<body>
  <div class="page">${body}</div>
</body>
</html>`;
}


// ============================================================================
// PDF TEMPLATES
// ============================================================================

function estimateResumeUnits(data) {
  // Each unit ≈ one line of text at base font size in PDF
  let units = 0;

  // Header: name + title + contact (fixed cost)
  units += 4;

  // Summary (~1 unit per 80 chars)
  if (data?.summary) units += Math.ceil(String(data.summary).length / 80) + 1;

  // Experience (heading=1, each item = header(2) + bullets)
  const exp = data?.experience || [];
  if (exp.length) {
    units += 2; // section heading
    exp.forEach(e => {
      units += 2; // title/company row
      units += (e?.bullets || []).length * 1.5;
    });
  }

  // Projects (heading=1, each = name + company + desc + bullets)
  const proj = data?.projects || [];
  if (proj.length) {
    units += 2;
    proj.forEach(p => {
      units += 2; // name + company
      if (p?.description) units += Math.ceil(String(p.description).length / 80);
      units += (p?.bullets || []).length * 1.5;
    });
  }

  // Education (heading=1, each = 3-4 lines)
  const edu = data?.education || [];
  if (edu.length) {
    units += 2;
    edu.forEach(e => {
      units += e?.gpa ? 4 : 3;
    });
  }

  // Skills (heading=1, pills wrap ~6 per row)
  const skills = data?.skills || [];
  if (skills.length) {
    units += 2;
    units += Math.ceil(skills.length / 5);
  }

  // Certifications (heading=1, each = 1 line)
  const certs = data?.certifications || [];
  if (certs.length) {
    units += 2;
    units += certs.length * 1.2;
  }

  // Publications (heading=1, each = 1-2 lines)
  const pubs = data?.publications || [];
  if (pubs.length) {
    units += 2;
    units += pubs.length * 1.5;
  }

  // Interests (heading=1, 1 line)
  if ((data?.interests || []).length) units += 3;

  // Additional sections (heading + items)
  (data?.additionalSections || []).forEach(s => {
    units += 2;
    units += (Array.isArray(s?.content) ? s.content.length : 1) * 1.2;
  });

  return units;
}

function getPdfScale(data, unitDiscount = 0) {
  const units = estimateResumeUnits(data) - unitDiscount;
  // A4 page at base font fits ~55 units; sidebar templates slightly less
  if (units > 70) return 0.72;
  if (units > 62) return 0.78;
  if (units > 55) return 0.83;
  if (units > 48) return 0.88;
  if (units > 42) return 0.93;
  return 1;
}

const getModernStyles = (s = 1) => StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica',
    fontSize: 10 * s,
    position: 'relative',
    paddingTop: 28 * s,
    paddingBottom: 28 * s,
  },

  // SIDEBAR BACKGROUND ONLY - fixed on all pages
  sidebarBackground: {
    position: 'absolute',
    left: 0,
    top: 28 * s,
    bottom: 28 * s,
    width: '30%',
    backgroundColor: '#f8fafc',
    borderRight: `${1 * s}px solid #e2e8f0`,
  },

  // PAGE CONTENT - two columns with proper spacing
  pageContent: {
    flexDirection: 'row',
  },

  // LEFT SIDEBAR CONTENT - renders ONCE (not fixed)
  sidebar: {
    width: '30%',
    padding: `0 ${16 * s}px 0 ${16 * s}px`,
  },

  // RIGHT MAIN CONTENT with proper padding INCLUDING TOP
  mainContent: {
    width: '70%',
    padding: `0 ${24 * s}px 0 ${24 * s}px`,
  },

  // Header
  name: {
    fontSize: 22 * s,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b',
    marginBottom: 3 * s,
  },
  title: {
    fontSize: 13 * s,
    color: '#4f46e5',
    fontFamily: 'Helvetica-Bold',
    marginBottom: 12 * s,
  },

  // Sidebar sections
  sideHeading: {
    fontSize: 11 * s,
    fontFamily: 'Helvetica-Bold',
    color: '#4f46e5',
    textTransform: 'uppercase',
    letterSpacing: 0.5 * s,
    borderBottom: `${1 * s}px solid #c7d2fe`,
    paddingBottom: 6 * s,
    marginBottom: 8 * s,
    marginTop: 16 * s,
  },
  firstSideHeading: {
    marginTop: 0,
  },

  // Contact with TEXT LABELS
  contactItem: {
    marginBottom: 8 * s,
  },
  contactLabel: {
    fontFamily: 'Helvetica-Bold',
    color: '#4f46e5',
    fontSize: 8 * s,
    textTransform: 'uppercase',
    marginBottom: 2 * s,
  },
  contactText: {
    fontSize: 9 * s,
    lineHeight: 1.4,
    color: '#475569',
  },
  urlLink: {
    fontSize: 8 * s,
    color: '#4f46e5',
    lineHeight: 1.3,
    textDecoration: 'none',
  },

  // Education
  eduItem: {
    marginBottom: 8 * s,
  },
  eduDegree: {
    fontSize: 10 * s,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b',
    lineHeight: 1.3,
  },
  eduField: {
    fontSize: 9 * s,
    color: '#475569',
    fontStyle: 'italic',
    marginTop: 1 * s,
  },
  eduInstitution: {
    fontSize: 9 * s,
    color: '#64748b',
    marginTop: 1 * s,
  },
  eduYear: {
    fontSize: 8 * s,
    color: '#94a3b8',
    fontStyle: 'italic',
    marginTop: 2 * s,
  },

  // Skills
  skillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 4 * s,
  },
  skillPill: {
    backgroundColor: '#ffffff',
    border: `${1 * s}px solid #e2e8f0`,
    borderRadius: 3 * s,
    padding: `${4 * s}px ${6 * s}px`,
    marginRight: 4 * s,
    marginBottom: 4 * s,
    fontSize: 8 * s,
    color: '#475569',
  },

  // Certifications
  certItem: {
    marginBottom: 5 * s,
  },
  certName: {
    fontSize: 9 * s,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b',
    lineHeight: 1.3,
  },
  certIssuer: {
    fontSize: 8 * s,
    color: '#64748b',
    marginTop: 1 * s,
  },

  // Main content sections
  sectionHeading: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6 * s,
    marginTop: 12 * s,
  },
  firstSectionHeading: {
    marginTop: 0,
  },
  sectionBar: {
    width: 3 * s,
    height: 14 * s,
    backgroundColor: '#4f46e5',
    marginRight: 6 * s,
    borderRadius: 1 * s,
  },
  sectionTitle: {
    fontSize: 12 * s,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    letterSpacing: 0.4 * s,
    color: '#1e293b',
  },

  // Summary
  summary: {
    fontSize: 9 * s,
    color: '#64748b',
    textAlign: 'justify',
    lineHeight: 1.5,
    marginBottom: 8 * s,
  },

  // Experience
  expItem: {
    marginBottom: 10 * s,
  },
  expHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2 * s,
  },
  expTitle: {
    fontSize: 10 * s,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b',
  },
  expDate: {
    fontSize: 8 * s,
    color: '#94a3b8',
  },
  expCompanyRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4 * s,
  },
  expCompany: {
    fontSize: 9 * s,
    color: '#4f46e5',
    fontFamily: 'Helvetica-Bold',
  },
  expLocation: {
    fontSize: 8 * s,
    color: '#94a3b8',
    fontStyle: 'italic',
  },
  bullet: {
    fontSize: 9 * s,
    color: '#64748b',
    marginBottom: 2 * s,
    marginLeft: 12 * s,
    lineHeight: 1.4,
  },

  // Projects
  projectItem: {
    marginBottom: 8 * s,
  },
  projectName: {
    fontSize: 10 * s,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b',
    marginBottom: 2 * s,
  },
  projectDesc: {
    fontSize: 9 * s,
    color: '#64748b',
    lineHeight: 1.4,
  },
});

const ModernPdfTemplate = ({ data }) => {
  const hasEducation = data.education && data.education.length > 0;
  const hasSkills = data.skills && data.skills.length > 0;
  const hasCertifications = data.certifications && data.certifications.length > 0;
  const hasProjects = data.projects && data.projects.length > 0;
  const s = getPdfScale(data);
  const styles = getModernStyles(s);

  return (
    <Page size="A4" style={styles.page}>
      {/* SIDEBAR BACKGROUND ONLY - fixed on all pages (empty, just color) */}
      <View style={styles.sidebarBackground} fixed />
      
      {/* PAGE CONTENT - two columns with TOP MARGIN (20px padding) */}
      <View style={styles.pageContent}>
        {/* LEFT SIDEBAR CONTENT - renders ONCE (not fixed) */}
        <View style={styles.sidebar}>
          {/* Contact Section with TEXT LABELS */}
          <Text style={[styles.sideHeading, styles.firstSideHeading]}>CONTACT</Text>

          {data.contact?.email && (
            <View style={styles.contactItem}>
              <Text style={styles.contactLabel}>EMAIL</Text>
              <Text style={styles.contactText}>{data.contact.email}</Text>
            </View>
          )}

          {data.contact?.phone && (
            <View style={styles.contactItem}>
              <Text style={styles.contactLabel}>PHONE</Text>
              <Text style={styles.contactText}>{data.contact.phone}</Text>
            </View>
          )}

          {data.contact?.location && (
            <View style={styles.contactItem}>
              <Text style={styles.contactLabel}>LOCATION</Text>
              <Text style={styles.contactText}>{data.contact.location}</Text>
            </View>
          )}

          {data.contact?.linkedin && (
            <View style={styles.contactItem}>
              <Text style={styles.contactLabel}>LINKEDIN</Text>
              <Link src={data.contact.linkedin} style={styles.urlLink}>
                {data.contact.linkedin}
              </Link>
            </View>
          )}

          {data.contact?.github && (
            <View style={styles.contactItem}>
              <Text style={styles.contactLabel}>GITHUB</Text>
              <Link src={data.contact.github} style={styles.urlLink}>
                {data.contact.github}
              </Link>
            </View>
          )}

          {/* Education Section */}
          {hasEducation && (
            <View>
              <Text style={styles.sideHeading}>EDUCATION</Text>
              {data.education.map((edu, i) => (
                <View key={i} style={styles.eduItem} wrap={false}>
                  <Text style={styles.eduDegree}>{edu.degree}</Text>
                  {edu.field && <Text style={styles.eduField}>{edu.field}</Text>}
                  <Text style={styles.eduInstitution}>{edu.institution}</Text>
                  {edu.years && <Text style={styles.eduYear}>{edu.years}</Text>}
                </View>
              ))}
            </View>
          )}

          {/* Skills Section */}
          {hasSkills && (
            <View>
              <Text style={styles.sideHeading}>SKILLS</Text>
              <View style={styles.skillsContainer}>
                {data.skills.map((skill, i) => (
                  <Text key={i} style={styles.skillPill}>{skill}</Text>
                ))}
              </View>
            </View>
          )}

          {/* Certifications Section */}
          {hasCertifications && (
            <View>
              <Text style={styles.sideHeading}>CERTIFICATIONS</Text>
              {data.certifications.map((cert, i) => (
                <View key={i} style={styles.certItem} wrap={false}>
                  <Text style={styles.certName}>{cert.name || cert}</Text>
                  {cert.issuer && <Text style={styles.certIssuer}>{cert.issuer}</Text>}
                </View>
              ))}
            </View>
          )}
        </View>

        {/* RIGHT MAIN CONTENT */}
        <View style={styles.mainContent}>
          {/* Header */}
          <View style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 10, marginBottom: 14 }}>
            <Text style={styles.name}>{data.name}</Text>
            {data.title && <Text style={styles.title}>{data.title}</Text>}
          </View>

          {/* Summary */}
          {data.summary && (
            <View>
              <View style={[styles.sectionHeading, styles.firstSectionHeading]}>
                <View style={styles.sectionBar} />
                <Text style={styles.sectionTitle}>PROFESSIONAL SUMMARY</Text>
              </View>
              <Text style={styles.summary}>{data.summary}</Text>
            </View>
          )}

          {/* Experience */}
          {data.experience && data.experience.length > 0 && (
            <View>
              <View style={data.summary ? styles.sectionHeading : [styles.sectionHeading, styles.firstSectionHeading]}>
                <View style={styles.sectionBar} />
                <Text style={styles.sectionTitle}>WORK EXPERIENCE</Text>
              </View>
              {data.experience.map((exp, i) => (
                <View key={i} style={styles.expItem} wrap={false}>
                  <View style={styles.expHeader}>
                    <Text style={styles.expTitle}>{exp.title}</Text>
                    <Text style={styles.expDate}>{exp.dates}</Text>
                  </View>
                  <View style={styles.expCompanyRow}>
                    <Text style={styles.expCompany}>{exp.company}</Text>
                    {exp.location && <Text style={styles.expLocation}>{exp.location}</Text>}
                  </View>
                  {exp.bullets && exp.bullets.map((bullet, j) => (
                    <Text key={j} style={styles.bullet}>• {bullet}</Text>
                  ))}
                </View>
              ))}
            </View>
          )}

          {/* Projects - No orphaned heading */}
          {hasProjects && (
            <View wrap={false}>
              <View style={styles.sectionHeading}>
                <View style={styles.sectionBar} />
                <Text style={styles.sectionTitle}>PROJECTS</Text>
              </View>
              {data.projects.slice(0, 1).map((proj, i) => (
                <View key={i} style={styles.projectItem}>
                  <Text style={styles.projectName}>{proj.name}</Text>
                  {proj.description && (
                    <Text style={styles.projectDesc}>
                      {Array.isArray(proj.description) ? proj.description.join(' ') : proj.description}
                    </Text>
                  )}
                  {proj.bullets && proj.bullets.length > 0 && proj.bullets.map((bullet, j) => (
                    <Text key={j} style={styles.bullet}>• {bullet}</Text>
                  ))}
                </View>
              ))}
            </View>
          )}

          {/* Remaining projects */}
          {hasProjects && data.projects.length > 1 && data.projects.slice(1).map((proj, i) => (
            <View key={i + 1} style={styles.projectItem} wrap={false}>
              <Text style={styles.projectName}>{proj.name}</Text>
              {proj.description && (
                <Text style={styles.projectDesc}>
                  {Array.isArray(proj.description) ? proj.description.join(' ') : proj.description}
                </Text>
              )}
              {proj.bullets && proj.bullets.length > 0 && proj.bullets.map((bullet, j) => (
                <Text key={j} style={styles.bullet}>• {bullet}</Text>
              ))}
            </View>
          ))}

          {/* Additional Sections */}
          {data.additionalSections && data.additionalSections.length > 0 && (
            <View style={{ marginTop: 16 }}>
              {data.additionalSections.map((section, idx) => (
                <View key={idx} style={{ marginTop: idx > 0 ? 12 : 0 }} wrap={false}>
                  <View style={styles.sectionHeading}>
                    <View style={styles.sectionBar} />
                    <Text style={styles.sectionTitle}>{section.heading.toUpperCase()}</Text>
                  </View>
                  {Array.isArray(section.content) ? (
                    <View>
                      {section.content.map((item, i) => (
                        <Text key={i} style={styles.bullet}>• {item}</Text>
                      ))}
                    </View>
                  ) : (
                    <Text style={styles.summary}>{section.content}</Text>
                  )}
                </View>
              ))}
            </View>
          )}

          {/* Publications */}
          {data.publications && data.publications.length > 0 && (
            <View wrap={false} style={{ marginTop: 12 }}>
              <View style={styles.sectionHeading}>
                <View style={styles.sectionBar} />
                <Text style={styles.sectionTitle}>PUBLICATIONS</Text>
              </View>
              {data.publications.map((pub, i) => (
                <View key={i} style={{ marginBottom: 4 * s }}>
                  <Text style={[styles.bullet, { fontFamily: 'Helvetica', marginLeft: 0 }]}>• {pub.title}{(pub.venue || pub.year) ? ` — ${[pub.venue, pub.year].filter(Boolean).join(', ')}` : ''}</Text>
                </View>
              ))}
            </View>
          )}

          {/* Interests */}
          {data.interests && data.interests.length > 0 && (
            <View wrap={false} style={{ marginTop: 12 }}>
              <View style={styles.sectionHeading}>
                <View style={styles.sectionBar} />
                <Text style={styles.sectionTitle}>INTERESTS</Text>
              </View>
              <Text style={styles.summary}>{data.interests.join(' • ')}</Text>
            </View>
          )}
        </View>
      </View>
    </Page>
  );
};

// ModernPdfTemplate defined

const getProfessionalStyles = (s = 1) => StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    padding: `${22 * s}px ${28 * s}px`,
    fontFamily: 'Times-Roman',
    fontSize: 10 * s,
  },

  // Header
  header: {
    textAlign: 'center',
    borderBottom: `${2 * s}px solid #111827`,
    paddingBottom: 8 * s,
    marginBottom: 12 * s,
  },
  name: {
    fontSize: 22 * s,
    fontFamily: 'Times-Bold',
    textTransform: 'uppercase',
    letterSpacing: 0.6 * s,
    marginBottom: 3 * s,
  },
  title: {
    fontSize: 11 * s,
    color: '#4b5563',
    fontFamily: 'Times-Bold',
    marginBottom: 6 * s,
  },

  // Contact - HORIZONTAL with TEXT LABELS
  contactRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: 4 * s,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 4 * s,
    marginBottom: 2 * s,
  },
  contactLabel: {
    fontFamily: 'Times-Bold',
    fontSize: 8 * s,
    marginRight: 3 * s,
    textTransform: 'uppercase',
    color: '#4b5563',
  },
  contactText: {
    fontSize: 9 * s,
    color: '#6b7280',
  },
  contactDot: {
    width: 3 * s,
    height: 3 * s,
    backgroundColor: '#9ca3af',
    borderRadius: 2 * s,
    marginHorizontal: 6 * s,
  },
  linkText: {
    fontSize: 8 * s,
    color: '#4f46e5',
    textDecoration: 'none',
  },

  // Section
  sectionHeading: {
    fontSize: 11 * s,
    fontFamily: 'Times-Bold',
    textTransform: 'uppercase',
    letterSpacing: 0.8 * s,
    borderBottom: `${1 * s}px solid #d1d5db`,
    paddingBottom: 4 * s,
    marginBottom: 6 * s,
    marginTop: 10 * s,
    color: '#111827',
  },
  firstSectionHeading: {
    marginTop: 0,
  },

  // Summary
  summary: {
    textAlign: 'justify',
    lineHeight: 1.4,
    marginBottom: 6 * s,
    fontSize: 9 * s,
  },

  // Experience
  expItem: {
    marginBottom: 8 * s,
  },
  expHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontFamily: 'Times-Bold',
    fontSize: 10 * s,
    marginBottom: 2 * s,
  },
  expSubheader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontFamily: 'Times-Italic',
    color: '#374151',
    fontSize: 9 * s,
    marginBottom: 4 * s,
  },
  bullet: {
    fontSize: 9 * s,
    color: '#374151',
    marginBottom: 2 * s,
    marginLeft: 12 * s,
    lineHeight: 1.4,
  },

  // Projects
  projectItem: {
    marginBottom: 8 * s,
  },
  projectName: {
    fontFamily: 'Times-Bold',
    fontSize: 10 * s,
    marginBottom: 2 * s,
  },
  projectDesc: {
    fontSize: 9 * s,
    color: '#374151',
    lineHeight: 1.4,
  },

  // Two column layout
  twoColumn: {
    flexDirection: 'row',
    gap: 18 * s,
    marginTop: 10 * s,
  },
  column: {
    flex: 1,
  },

  // Education
  eduItem: {
    marginBottom: 6 * s,
  },
  eduInstitution: {
    fontFamily: 'Times-Bold',
    fontSize: 10 * s,
  },
  eduDegree: {
    fontFamily: 'Times-Italic',
    fontSize: 9 * s,
    marginTop: 1 * s,
  },
  eduYear: {
    color: '#6b7280',
    fontSize: 8 * s,
    marginTop: 1 * s,
  },
  eduGpa: {
    color: '#374151',
    fontSize: 8 * s,
    marginTop: 1 * s,
  },

  // Skills
  skillsText: {
    fontSize: 9 * s,
    color: '#374151',
    lineHeight: 1.7,
  },

  // Certifications
  certItem: {
    fontSize: 9 * s,
    marginBottom: 3 * s,
  },
});

const ProfessionalPdfTemplate = ({ data }) => {
  const hasSkills = data.skills && data.skills.length > 0;
  const hasProjects = data.projects && data.projects.length > 0;
  const hasCertifications = data.certifications && data.certifications.length > 0;
  // Education+Skills are side-by-side in Professional, so subtract skills height from unit count
  const skillsDiscount = hasSkills ? 2 + Math.ceil((data.skills || []).length / 5) : 0;
  const s = getPdfScale(data, skillsDiscount);
  const styles = getProfessionalStyles(s);

  return (
    <Page size="A4" style={styles.page}>
      {/* Header with HORIZONTAL contact and TEXT LABELS */}
      <View style={styles.header}>
        <Text style={styles.name}>{data.name}</Text>
        {data.title && <Text style={styles.title}>{data.title}</Text>}

        {/* Contact Info - HORIZONTAL with EMAIL, PHONE, LOCATION labels */}
        <View style={styles.contactRow}>
          {data.contact?.email && (
            <View style={styles.contactItem}>
              <Text style={styles.contactLabel}>EMAIL:</Text>
              <Text style={styles.contactText}>{data.contact.email}</Text>
            </View>
          )}

          {data.contact?.phone && data.contact?.email && (
            <View style={styles.contactDot} />
          )}

          {data.contact?.phone && (
            <View style={styles.contactItem}>
              <Text style={styles.contactLabel}>PHONE:</Text>
              <Text style={styles.contactText}>{data.contact.phone}</Text>
            </View>
          )}

          {data.contact?.location && (data.contact?.email || data.contact?.phone) && (
            <View style={styles.contactDot} />
          )}

          {data.contact?.location && (
            <View style={styles.contactItem}>
              <Text style={styles.contactLabel}>LOCATION:</Text>
              <Text style={styles.contactText}>{data.contact.location}</Text>
            </View>
          )}
        </View>

        {/* LinkedIn and GitHub on second line with TEXT LABELS */}
        {(data.contact?.linkedin || data.contact?.github) && (
          <View style={[styles.contactRow, { marginTop: 3 }]}>
            {data.contact.linkedin && (
              <View style={styles.contactItem}>
                <Text style={styles.contactLabel}>LINKEDIN:</Text>
                <Link src={data.contact.linkedin} style={styles.linkText}>
                  {data.contact.linkedin}
                </Link>
              </View>
            )}
            {data.contact.github && data.contact.linkedin && (
              <View style={styles.contactDot} />
            )}
            {data.contact.github && (
              <View style={styles.contactItem}>
                <Text style={styles.contactLabel}>GITHUB:</Text>
                <Link src={data.contact.github} style={styles.linkText}>
                  {data.contact.github}
                </Link>
              </View>
            )}
          </View>
        )}
      </View>

      {/* Summary */}
      {data.summary && (
        <View>
          <Text style={[styles.sectionHeading, styles.firstSectionHeading]}>SUMMARY</Text>
          <Text style={styles.summary}>{data.summary}</Text>
        </View>
      )}

      {/* Experience */}
      {data.experience && data.experience.length > 0 && (
        <View>
          <Text style={data.summary ? styles.sectionHeading : [styles.sectionHeading, styles.firstSectionHeading]}>EXPERIENCE</Text>
          {data.experience.map((exp, i) => (
            <View key={i} style={styles.expItem} wrap={false}>
              <View style={styles.expHeader}>
                <Text>{exp.company}</Text>
                <Text>{exp.dates}</Text>
              </View>
              <View style={styles.expSubheader}>
                <Text>{exp.title}</Text>
                {exp.location && <Text>{exp.location}</Text>}
              </View>
              {exp.bullets && exp.bullets.map((bullet, j) => (
                <Text key={j} style={styles.bullet}>• {bullet}</Text>
              ))}
            </View>
          ))}
        </View>
      )}

      {/* Projects - No orphaned heading */}
      {hasProjects && (
        <View wrap={false}>
          <Text style={styles.sectionHeading}>PROJECTS</Text>
          {data.projects.slice(0, 1).map((proj, i) => (
            <View key={i} style={styles.projectItem}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={styles.projectName}>{proj.name}</Text>
                {proj.dates && <Text style={{ fontSize: 8 * s, color: '#6b7280' }}>{proj.dates}</Text>}
              </View>
              {proj.company && <Text style={{ fontSize: 9 * s, color: '#374151', fontFamily: 'Times-Italic', marginBottom: 2 * s }}>{proj.company}</Text>}
              {proj.description && (
                <Text style={styles.projectDesc}>
                  {Array.isArray(proj.description) ? proj.description.join(' ') : proj.description}
                </Text>
              )}
              {proj.bullets && proj.bullets.length > 0 && proj.bullets.map((bullet, j) => (
                <Text key={j} style={styles.bullet}>• {bullet}</Text>
              ))}
            </View>
          ))}
        </View>
      )}

      {/* Remaining projects */}
      {hasProjects && data.projects.length > 1 && data.projects.slice(1).map((proj, i) => (
        <View key={i + 1} style={styles.projectItem} wrap={false}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={styles.projectName}>{proj.name}</Text>
            {proj.dates && <Text style={{ fontSize: 8 * s, color: '#6b7280' }}>{proj.dates}</Text>}
          </View>
          {proj.company && <Text style={{ fontSize: 9 * s, color: '#374151', fontFamily: 'Times-Italic', marginBottom: 2 * s }}>{proj.company}</Text>}
          {proj.description && (
            <Text style={styles.projectDesc}>
              {Array.isArray(proj.description) ? proj.description.join(' ') : proj.description}
            </Text>
          )}
          {proj.bullets && proj.bullets.length > 0 && proj.bullets.map((bullet, j) => (
            <Text key={j} style={styles.bullet}>• {bullet}</Text>
          ))}
        </View>
      ))}

      {/* Education & Skills */}
      <View style={styles.twoColumn}>
        {data.education && data.education.length > 0 && (
          <View style={styles.column}>
            <Text style={styles.sectionHeading}>EDUCATION</Text>
            {data.education.map((edu, i) => (
              <View key={i} style={styles.eduItem} wrap={false}>
                <Text style={styles.eduInstitution}>{edu.institution}</Text>
                <Text style={styles.eduDegree}>
                  {edu.degree}{edu.field ? ` in ${edu.field}` : ''}
                </Text>
                {edu.years && <Text style={styles.eduYear}>{edu.years}</Text>}
                {edu.gpa && <Text style={styles.eduGpa}>{edu.gpa}</Text>}
              </View>
            ))}
          </View>
        )}

        {hasSkills && (
          <View style={styles.column}>
            <Text style={styles.sectionHeading}>SKILLS</Text>
            <Text style={styles.skillsText}>
              {data.skills.join(' • ')}
            </Text>
          </View>
        )}
      </View>

      {/* Certifications */}
      {hasCertifications && (
        <View>
          <Text style={styles.sectionHeading}>CERTIFICATIONS</Text>
          {data.certifications.map((cert, i) => (
            <Text key={i} style={styles.certItem}>
              • {cert.name || cert}{cert.issuer ? ` – ${cert.issuer}` : ''}
            </Text>
          ))}
        </View>
      )}
      {/* Additional Sections */}
      {data.additionalSections && data.additionalSections.length > 0 && (
        <View style={{ marginTop: 16 }}>
          {data.additionalSections.map((section, idx) => (
            <View key={idx} style={{ marginTop: idx > 0 ? 12 : 0 }} wrap={false}>
              <Text style={styles.sectionHeading}>{section.heading.toUpperCase()}</Text>
              {Array.isArray(section.content) ? (
                <View>
                  {section.content.map((item, i) => (
                    <Text key={i} style={{ fontSize: 9 * s, marginLeft: 12 * s, marginBottom: 3 * s, lineHeight: 1.5 }}>• {item}</Text>
                  ))}
                </View>
              ) : (
                <Text style={{ fontSize: 9 * s, lineHeight: 1.5, marginTop: 4 * s }}>{section.content}</Text>
              )}
            </View>
          ))}
        </View>
      )}

      {/* Publications */}
      {data.publications && data.publications.length > 0 && (
        <View wrap={false} style={{ marginTop: 12 * s }}>
          <Text style={styles.sectionHeading}>PUBLICATIONS</Text>
          {data.publications.map((pub, i) => (
            <Text key={i} style={{ fontSize: 9 * s, color: '#374151', marginBottom: 3 * s, lineHeight: 1.5 }}>
              • {pub.title}{(pub.venue || pub.year) ? ` — ${[pub.venue, pub.year].filter(Boolean).join(', ')}` : ''}
            </Text>
          ))}
        </View>
      )}

      {/* Interests */}
      {data.interests && data.interests.length > 0 && (
        <View wrap={false} style={{ marginTop: 12 * s }}>
          <Text style={styles.sectionHeading}>INTERESTS</Text>
          <Text style={{ fontSize: 9 * s, color: '#374151', lineHeight: 1.5 }}>{data.interests.join(' • ')}</Text>
        </View>
      )}
    </Page>
  );
};

// ProfessionalPdfTemplate defined

const getCreativeStyles = (s = 1) => StyleSheet.create({
  page: {
    backgroundColor: '#faf5ff',
    paddingTop: 18 * s,
    paddingLeft: 18 * s,
    paddingRight: 18 * s,
    paddingBottom: 18 * s,
    fontFamily: 'Helvetica',
    fontSize: 10 * s,
    display: 'flex',
    flexDirection: 'column',
  },

  // Content wrapper - flex 1 to fill available space
  content: {
    backgroundColor: '#ffffff',
    borderRadius: 12 * s,
    padding: `${22 * s}px`,
    flex: 1,
  },

  // Purple header
  header: {
    backgroundColor: '#7c3aed',
    borderRadius: 10 * s,
    padding: `${18 * s}px`,
    marginBottom: 16 * s,
    color: '#ffffff',
  },
  name: {
    fontSize: 24 * s,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 6 * s,
  },
  title: {
    fontSize: 13 * s,
    fontFamily: 'Helvetica-Bold',
    opacity: 0.95,
    marginBottom: 10 * s,
  },

  // Contact - HORIZONTAL with TEXT LABELS
  contactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    fontSize: 11 * s,
    opacity: 0.95,
    marginTop: 8 * s,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12 * s,
    marginBottom: 3 * s,
  },
  contactLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8 * s,
    marginRight: 4 * s,
    textTransform: 'uppercase',
    letterSpacing: 0.3 * s,
  },
  contactText: {
    fontSize: 10 * s,
  },
  linkText: {
    fontSize: 8 * s,
    textDecoration: 'none',
    color: '#ffffff',
  },

  // Section heading
  sectionHeading: {
    fontSize: 11 * s,
    fontFamily: 'Helvetica-Bold',
    color: '#6d28d9',
    textTransform: 'uppercase',
    letterSpacing: 0.5 * s,
    marginBottom: 6 * s,
    marginTop: 10 * s,
  },
  firstSectionHeading: {
    marginTop: 0,
  },

  // Summary
  summary: {
    color: '#64748b',
    textAlign: 'justify',
    lineHeight: 1.5,
    marginBottom: 10 * s,
    fontSize: 9 * s,
  },

  // Experience
  expItem: {
    backgroundColor: '#f5f3ff',
    borderRadius: 6 * s,
    padding: `${10 * s}px`,
    marginBottom: 6 * s,
    marginTop: 2 * s,
  },
  expHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4 * s,
  },
  expTitle: {
    fontSize: 10 * s,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b',
  },
  expCompany: {
    fontSize: 9 * s,
    color: '#db2777',
    fontFamily: 'Helvetica-Bold',
  },
  expDate: {
    fontSize: 8 * s,
    color: '#64748b',
  },
  bullet: {
    fontSize: 9 * s,
    color: '#64748b',
    marginBottom: 2 * s,
    marginLeft: 10 * s,
    lineHeight: 1.4,
  },

  // Projects
  projectItem: {
    backgroundColor: '#fdf2f8',
    borderRadius: 6 * s,
    padding: `${8 * s}px`,
    marginBottom: 5 * s,
    marginTop: 2 * s,
  },
  projectName: {
    fontSize: 10 * s,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b',
    marginBottom: 2 * s,
  },
  projectDesc: {
    fontSize: 9 * s,
    color: '#64748b',
    lineHeight: 1.4,
  },

  // Two column
  twoColumn: {
    flexDirection: 'row',
    gap: 12 * s,
    marginTop: 8 * s,
  },
  column: {
    flex: 1,
    backgroundColor: '#f8fafc',
    borderRadius: 6 * s,
    padding: `${10 * s}px`,
  },

  // Education
  eduItem: {
    marginBottom: 6 * s,
  },
  eduDegree: {
    fontSize: 9 * s,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b',
    lineHeight: 1.3,
  },
  eduInstitution: {
    fontSize: 9 * s,
    color: '#64748b',
    marginTop: 1 * s,
  },
  eduYear: {
    fontSize: 8 * s,
    color: '#94a3b8',
    marginTop: 1 * s,
  },

  // Skills
  skillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  skillPill: {
    backgroundColor: '#ffffff',
    border: `${1 * s}px solid #ede9fe`,
    borderRadius: 3 * s,
    padding: `${4 * s}px ${6 * s}px`,
    marginRight: 4 * s,
    marginBottom: 4 * s,
    fontSize: 8 * s,
    color: '#475569',
    fontFamily: 'Helvetica-Bold',
  },

  // Certifications
  certItem: {
    fontSize: 9 * s,
    color: '#475569',
    marginBottom: 3 * s,
  },
});

const CreativePdfTemplate = ({ data }) => {
  const hasSkills = data.skills && data.skills.length > 0;
  const hasProjects = data.projects && data.projects.length > 0;
  const hasCertifications = data.certifications && data.certifications.length > 0;
  const hasEducation = data.education && data.education.length > 0;
  const s = getPdfScale(data);
  const styles = getCreativeStyles(s);

  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.content}>
        {/* Purple header with HORIZONTAL contact and TEXT LABELS */}
        <View style={styles.header}>
          <Text style={styles.name}>{data.name}</Text>
          {data.title && <Text style={styles.title}>{data.title}</Text>}

          {/* Contact Info - HORIZONTAL with EMAIL, PHONE, LOCATION labels */}
          <View style={styles.contactRow}>
            {data.contact?.email && (
              <View style={styles.contactItem}>
                <Text style={styles.contactLabel}>EMAIL:</Text>
                <Text style={styles.contactText}>{data.contact.email}</Text>
              </View>
            )}

            {data.contact?.phone && (
              <View style={styles.contactItem}>
                <Text style={styles.contactLabel}>PHONE:</Text>
                <Text style={styles.contactText}>{data.contact.phone}</Text>
              </View>
            )}

            {data.contact?.location && (
              <View style={styles.contactItem}>
                <Text style={styles.contactLabel}>LOCATION:</Text>
                <Text style={styles.contactText}>{data.contact.location}</Text>
              </View>
            )}
          </View>

          {/* LinkedIn and GitHub on second line with TEXT LABELS */}
          {(data.contact?.linkedin || data.contact?.github) && (
            <View style={[styles.contactRow, { marginTop: 4 }]}>
              {data.contact.linkedin && (
                <View style={styles.contactItem}>
                  <Text style={styles.contactLabel}>LINKEDIN:</Text>
                  <Link src={data.contact.linkedin} style={styles.linkText}>
                    {data.contact.linkedin}
                  </Link>
                </View>
              )}
              {data.contact.github && (
                <View style={styles.contactItem}>
                  <Text style={styles.contactLabel}>GITHUB:</Text>
                  <Link src={data.contact.github} style={styles.linkText}>
                    {data.contact.github}
                  </Link>
                </View>
              )}
            </View>
          )}
        </View>

        {/* Summary */}
        {data.summary && (
          <View>
            <Text style={[styles.sectionHeading, styles.firstSectionHeading]}>ABOUT</Text>
            <Text style={styles.summary}>{data.summary}</Text>
          </View>
        )}

        {/* Experience */}
        {data.experience && data.experience.length > 0 && (
          <View>
            <Text style={data.summary ? styles.sectionHeading : [styles.sectionHeading, styles.firstSectionHeading]}>EXPERIENCE</Text>
            {data.experience.map((exp, i) => (
              <View key={i} style={styles.expItem} wrap={false}>
                <View style={styles.expHeader}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.expTitle}>{exp.title}</Text>
                    <Text style={styles.expCompany}>{exp.company}</Text>
                  </View>
                  <Text style={styles.expDate}>{exp.dates}</Text>
                </View>
                {exp.bullets && exp.bullets.map((bullet, j) => (
                  <Text key={j} style={styles.bullet}>• {bullet}</Text>
                ))}
              </View>
            ))}
          </View>
        )}

        {/* Projects - No orphaned heading */}
        {hasProjects && (
          <View wrap={false}>
            <Text style={styles.sectionHeading}>PROJECTS</Text>
            {data.projects.slice(0, 1).map((proj, i) => (
              <View key={i} style={styles.projectItem}>
                <Text style={styles.projectName}>{proj.name}</Text>
                {proj.description && (
                  <Text style={styles.projectDesc}>
                    {Array.isArray(proj.description) ? proj.description.join(' ') : proj.description}
                  </Text>
                )}
                {proj.bullets && proj.bullets.length > 0 && proj.bullets.map((bullet, j) => (
                  <Text key={j} style={styles.bullet}>• {bullet}</Text>
                ))}
              </View>
            ))}
          </View>
        )}

        {/* Remaining projects */}
        {hasProjects && data.projects.length > 1 && data.projects.slice(1).map((proj, i) => (
          <View key={i + 1} style={styles.projectItem} wrap={false}>
            <Text style={styles.projectName}>{proj.name}</Text>
            {proj.description && (
              <Text style={styles.projectDesc}>
                {Array.isArray(proj.description) ? proj.description.join(' ') : proj.description}
              </Text>
            )}
            {proj.bullets && proj.bullets.length > 0 && proj.bullets.map((bullet, j) => (
              <Text key={j} style={styles.bullet}>• {bullet}</Text>
            ))}
          </View>
        ))}

        {/* Education & Skills - Keep sections together */}
        {(hasEducation || hasSkills) && (
          <View style={styles.twoColumn} wrap={false}>
            {hasEducation && (
              <View style={styles.column}>
                <Text style={styles.sectionHeading}>EDUCATION</Text>
                {data.education.map((edu, i) => (
                  <View key={i} style={styles.eduItem}>
                    <Text style={styles.eduDegree}>{edu.degree}</Text>
                    <Text style={styles.eduInstitution}>{edu.institution}</Text>
                    {edu.years && <Text style={styles.eduYear}>{edu.years}</Text>}
                  </View>
                ))}
              </View>
            )}

            {hasSkills && (
              <View style={styles.column}>
                <Text style={styles.sectionHeading}>SKILLS</Text>
                <View style={styles.skillsContainer}>
                  {data.skills.map((skill, i) => (
                    <Text key={i} style={styles.skillPill}>{skill}</Text>
                  ))}
                </View>
              </View>
            )}
          </View>
        )}

        {/* Certifications */}
        {hasCertifications && (
          <View style={{ marginTop: 8 }}>
            <Text style={styles.sectionHeading}>CERTIFICATIONS</Text>
            {data.certifications.map((cert, i) => (
              <Text key={i} style={styles.certItem}>
                • {cert.name || cert}{cert.issuer ? ` – ${cert.issuer}` : ''}
              </Text>
            ))}
          </View>
        )}

        {/* Additional Sections */}
        {data.additionalSections && data.additionalSections.length > 0 && (
          <View style={{ marginTop: 16 }}>
            {data.additionalSections.map((section, idx) => (
              <View key={idx} style={{ marginTop: idx > 0 ? 12 : 0 }} wrap={false}>
                <Text style={styles.sectionHeading}>{section.heading.toUpperCase()}</Text>
                {Array.isArray(section.content) ? (
                  <View>
                    {section.content.map((item, i) => (
                      <Text key={i} style={styles.bullet}>• {item}</Text>
                    ))}
                  </View>
                ) : (
                  <Text style={styles.summary}>{section.content}</Text>
                )}
              </View>
            ))}
          </View>
        )}

        {/* Publications */}
        {data.publications && data.publications.length > 0 && (
          <View wrap={false} style={{ marginTop: 10 * s }}>
            <Text style={styles.sectionHeading}>PUBLICATIONS</Text>
            {data.publications.map((pub, i) => (
              <Text key={i} style={[styles.bullet, { marginLeft: 0 }]}>
                • {pub.title}{(pub.venue || pub.year) ? ` — ${[pub.venue, pub.year].filter(Boolean).join(', ')}` : ''}
              </Text>
            ))}
          </View>
        )}

        {/* Interests */}
        {data.interests && data.interests.length > 0 && (
          <View wrap={false} style={{ marginTop: 10 * s }}>
            <Text style={styles.sectionHeading}>INTERESTS</Text>
            <Text style={styles.summary}>{data.interests.join(' • ')}</Text>
          </View>
        )}
      </View>
    </Page>
  );
};

// CreativePdfTemplate defined

const getMinimalStyles = (s = 1) => StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    padding: `${30 * s}px ${32 * s}px`,
    fontFamily: 'Helvetica',
    fontSize: 10 * s,
  },

  // Header
  header: {
    marginBottom: 24 * s,
  },
  name: {
    fontSize: 26 * s,
    fontWeight: 'light',
    letterSpacing: -0.5 * s,
    color: '#000000',
    marginBottom: 3 * s,
  },
  title: {
    fontSize: 12 * s,
    color: '#6b7280',
    fontWeight: 'light',
    marginTop: 3 * s,
  },
  contactGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 8 * s,
    fontSize: 9 * s,
    color: '#6b7280',
    maxWidth: 480,
  },
  contactItem: {
    width: '50%',
    marginBottom: 2 * s,
  },
  contactUrl: {
    fontSize: 8 * s,
  },

  // Two column layout
  twoColumn: {
    flexDirection: 'row',
    gap: 28 * s,
  },
  leftColumn: {
    width: '30%',
  },
  rightColumn: {
    width: '70%',
  },

  // Section heading
  sectionHeading: {
    fontFamily: 'Helvetica-Bold',
    color: '#000000',
    textTransform: 'uppercase',
    letterSpacing: 0.8 * s,
    fontSize: 10 * s,
    marginBottom: 6 * s,
    marginTop: 14 * s,
  },
  firstSectionHeading: {
    marginTop: 0,
  },

  // Education (left column)
  eduItem: {
    marginBottom: 7 * s,
  },
  eduInstitution: {
    fontSize: 9 * s,
    fontFamily: 'Helvetica-Bold',
    color: '#000000',
    lineHeight: 1.3,
  },
  eduDegree: {
    fontSize: 9 * s,
    color: '#6b7280',
    marginTop: 1 * s,
  },
  eduYear: {
    fontSize: 8 * s,
    color: '#9ca3af',
    marginTop: 2 * s,
  },

  // Skills
  skillItem: {
    fontSize: 9 * s,
    color: '#6b7280',
    marginBottom: 3 * s,
  },

  // Certifications
  certItem: {
    fontSize: 9 * s,
    color: '#6b7280',
    marginBottom: 3 * s,
  },

  // Summary
  summary: {
    color: '#6b7280',
    textAlign: 'justify',
    lineHeight: 1.5,
    marginBottom: 12 * s,
    fontSize: 9 * s,
  },

  // Experience (right column)
  expItem: {
    marginBottom: 12 * s,
  },
  expHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2 * s,
  },
  expTitle: {
    fontSize: 10 * s,
    fontFamily: 'Helvetica-Bold',
    color: '#000000',
  },
  expDate: {
    fontSize: 8 * s,
    color: '#9ca3af',
  },
  expCompany: {
    fontSize: 9 * s,
    color: '#6b7280',
    marginBottom: 4 * s,
  },
  bullet: {
    fontSize: 9 * s,
    color: '#6b7280',
    marginBottom: 2 * s,
    marginLeft: 12 * s,
    lineHeight: 1.4,
  },

  // Projects
  projectItem: {
    marginBottom: 8 * s,
  },
  projectName: {
    fontSize: 10 * s,
    fontFamily: 'Helvetica-Bold',
    color: '#000000',
    marginBottom: 2 * s,
  },
  projectDesc: {
    fontSize: 9 * s,
    color: '#6b7280',
    lineHeight: 1.4,
  },
});

const MinimalPdfTemplate = ({ data }) => {
  const hasSkills = data.skills && data.skills.length > 0;
  const hasProjects = data.projects && data.projects.length > 0;
  const hasCertifications = data.certifications && data.certifications.length > 0;
  const s = getPdfScale(data);
  const styles = getMinimalStyles(s);

  return (
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.name}>{data.name}</Text>
        {data.title && <Text style={styles.title}>{data.title}</Text>}

        <View style={styles.contactGrid}>
          {data.contact?.email && <Text style={styles.contactItem}>{data.contact.email}</Text>}
          {data.contact?.phone && <Text style={styles.contactItem}>{data.contact.phone}</Text>}
          {data.contact?.location && <Text style={styles.contactItem}>{data.contact.location}</Text>}
          {data.contact?.linkedin && <Text style={[styles.contactItem, styles.contactUrl]}>{data.contact.linkedin}</Text>}
          {data.contact?.github && <Text style={[styles.contactItem, styles.contactUrl]}>{data.contact.github}</Text>}
        </View>
      </View>

      {/* Two Column Layout */}
      <View style={styles.twoColumn}>
        {/* Left Column */}
        <View style={styles.leftColumn}>
          {/* Education */}
          {data.education && data.education.length > 0 && (
            <View>
              <Text style={[styles.sectionHeading, styles.firstSectionHeading]}>EDUCATION</Text>
              {data.education.map((edu, i) => (
                <View key={i} style={styles.eduItem} wrap={false}>
                  <Text style={styles.eduInstitution}>{edu.institution}</Text>
                  <Text style={styles.eduDegree}>
                    {edu.degree}{edu.field ? ` – ${edu.field}` : ''}
                  </Text>
                  {edu.years && <Text style={styles.eduYear}>{edu.years}</Text>}
                </View>
              ))}
            </View>
          )}

          {/* Skills */}
          {hasSkills && (
            <View>
              <Text style={styles.sectionHeading}>SKILLS</Text>
              {data.skills.map((skill, i) => (
                <Text key={i} style={styles.skillItem}>{skill}</Text>
              ))}
            </View>
          )}

          {/* Certifications */}
          {hasCertifications && (
            <View>
              <Text style={styles.sectionHeading}>CERTIFICATIONS</Text>
              {data.certifications.map((cert, i) => (
                <Text key={i} style={styles.certItem}>
                  {cert.name || cert}{cert.issuer ? ` – ${cert.issuer}` : ''}
                </Text>
              ))}
            </View>
          )}
        </View>

        {/* Right Column */}
        <View style={styles.rightColumn}>
          {/* Summary */}
          {data.summary && (
            <View>
              <Text style={[styles.sectionHeading, styles.firstSectionHeading]}>PROFILE</Text>
              <Text style={styles.summary}>{data.summary}</Text>
            </View>
          )}

          {/* Experience */}
          {data.experience && data.experience.length > 0 && (
            <View>
              <Text style={data.summary ? styles.sectionHeading : [styles.sectionHeading, styles.firstSectionHeading]}>EXPERIENCE</Text>
              {data.experience.map((exp, i) => (
                <View key={i} style={styles.expItem} wrap={false}>
                  <View style={styles.expHeader}>
                    <Text style={styles.expTitle}>{exp.title}</Text>
                    <Text style={styles.expDate}>{exp.dates}</Text>
                  </View>
                  <Text style={styles.expCompany}>
                    {exp.company}{exp.location ? ` — ${exp.location}` : ''}
                  </Text>
                  {exp.bullets && exp.bullets.map((bullet, j) => (
                    <Text key={j} style={styles.bullet}>• {bullet}</Text>
                  ))}
                </View>
              ))}
            </View>
          )}

          {/* Projects - No orphaned heading */}
          {hasProjects && (
            <View wrap={false}>
              <Text style={styles.sectionHeading}>PROJECTS</Text>
              {data.projects.slice(0, 1).map((proj, i) => (
                <View key={i} style={styles.projectItem}>
                  <Text style={styles.projectName}>{proj.name}</Text>
                  {proj.description && (
                    <Text style={styles.projectDesc}>
                      {Array.isArray(proj.description) ? proj.description.join(' ') : proj.description}
                    </Text>
                  )}
                  {proj.bullets && proj.bullets.length > 0 && proj.bullets.map((bullet, j) => (
                    <Text key={j} style={styles.bullet}>• {bullet}</Text>
                  ))}
                </View>
              ))}
            </View>
          )}

          {/* Remaining projects */}
          {hasProjects && data.projects.length > 1 && data.projects.slice(1).map((proj, i) => (
            <View key={i + 1} style={styles.projectItem} wrap={false}>
              <Text style={styles.projectName}>{proj.name}</Text>
              {proj.description && (
                <Text style={styles.projectDesc}>
                  {Array.isArray(proj.description) ? proj.description.join(' ') : proj.description}
                </Text>
              )}
              {proj.bullets && proj.bullets.length > 0 && proj.bullets.map((bullet, j) => (
                <Text key={j} style={styles.bullet}>• {bullet}</Text>
              ))}
            </View>
          ))}
        </View>
      </View>
      {/* Additional Sections */}
      {data.additionalSections && data.additionalSections.length > 0 && (
        <View style={{ marginTop: 16 }}>
          {data.additionalSections.map((section, idx) => (
            <View key={idx} style={{ marginTop: idx > 0 ? 12 : 0 }} wrap={false}>
              <Text style={styles.sectionHeading}>{section.heading.toUpperCase()}</Text>
              {Array.isArray(section.content) ? (
                <View>
                  {section.content.map((item, i) => (
                    <Text key={i} style={{ fontSize: 9 * s, marginLeft: 12 * s, marginBottom: 3 * s, lineHeight: 1.5 }}>• {item}</Text>
                  ))}
                </View>
              ) : (
                <Text style={{ fontSize: 9 * s, lineHeight: 1.5, marginTop: 4 * s }}>{section.content}</Text>
              )}
            </View>
          ))}
        </View>
      )}

      {/* Publications */}
      {data.publications && data.publications.length > 0 && (
        <View wrap={false} style={{ marginTop: 14 * s }}>
          <Text style={styles.sectionHeading}>PUBLICATIONS</Text>
          {data.publications.map((pub, i) => (
            <Text key={i} style={{ fontSize: 9 * s, color: '#6b7280', marginBottom: 3 * s, lineHeight: 1.5 }}>
              • {pub.title}{(pub.venue || pub.year) ? ` — ${[pub.venue, pub.year].filter(Boolean).join(', ')}` : ''}
            </Text>
          ))}
        </View>
      )}

      {/* Interests */}
      {data.interests && data.interests.length > 0 && (
        <View wrap={false} style={{ marginTop: 14 * s }}>
          <Text style={styles.sectionHeading}>INTERESTS</Text>
          <Text style={{ fontSize: 9 * s, color: '#6b7280', lineHeight: 1.5 }}>{data.interests.join(' • ')}</Text>
        </View>
      )}
    </Page>
  );
};

// MinimalPdfTemplate defined


// ============================================================================
// PDF EXPORT FUNCTIONALITY
// ============================================================================

export async function exportResumePdf(resumeData, templateId, filename = 'resume.pdf') {
  try {
    console.log('Starting PDF export...', { resumeData, templateId, filename });
    
    const TemplateComponent = getPdfTemplate(templateId);
    console.log('Template component retrieved:', TemplateComponent?.name);
    
    if (!TemplateComponent) {
      throw new Error(`Template not found: ${templateId}`);
    }
    
    console.log('Creating PDF document...');
    const blob = await pdf(
      React.createElement(Document, null, React.createElement(TemplateComponent, { data: resumeData }))
    ).toBlob();
    
    console.log('PDF blob created:', blob.size, 'bytes');
    
    saveAs(blob, filename);
    console.log('PDF download triggered');
    
    return { success: true };
  } catch (error) {
    console.error('PDF export failed:', error);
    throw error;
  }
}

export async function getResumePdfBlob(resumeData, templateId) {
  try {
    const TemplateComponent = getPdfTemplate(templateId);
    const blob = await pdf(
      React.createElement(Document, null, React.createElement(TemplateComponent, { data: resumeData }))
    ).toBlob();
    return blob;
  } catch (error) {
    console.error('PDF generation failed:', error);
    throw error;
  }
}

/**
 * Send resume via email with user details for lead generation
 * @param {Object} params - Email parameters
 * @param {string} params.name - User's name (optional)
 * @param {string} params.companyName - Company name (optional)
 * @param {string} params.email - User's email (required)
 * @param {string} params.template - Selected template (required)
 * @param {Blob} params.pdfBlob - PDF blob to send (required)
 * @param {string} params.filename - PDF filename (required)
 * @returns {Promise<Object>} Response from server
 */
export async function sendResumeViaEmail({ name, companyName, email, template, pdfBlob, filename }) {
  try {
    const baseUrl = getApiBaseUrl();
    
    // Create FormData
    const formData = new FormData();
    formData.append('name', name || '');
    formData.append('company_name', companyName || '');
    formData.append('email', email);
    formData.append('template', template);
    formData.append('pdf_file', pdfBlob, filename);
    
    // Send request
    const response = await fetch(`${baseUrl}/send_resume_via_email/`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    
    const data = await safeJson(response);
    
    if (!response.ok) {
      throw new Error(data?.error || data?.message || 'Failed to send email');
    }
    
    return data;
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
}

function getPdfTemplate(templateId) {
  const templates = {
    modern: ModernPdfTemplate,
    professional: ProfessionalPdfTemplate,
    creative: CreativePdfTemplate,
    minimal: MinimalPdfTemplate,
  };
  return templates[templateId] || templates.modern;
}

// ============================================================================
// NOTE: Additional Sections Feature
// ============================================================================
// The PDF templates above are complete and functional.
// However, they do NOT yet render data.additionalSections.
// 
// To add support for additionalSections (Hobbies, Extra Activities, 
// Personal Details, Declaration, etc.), insert the following code
// before the closing </Page> tag in each template:
//
// {data.additionalSections && data.additionalSections.length > 0 && (
//   <View style={{ marginTop: 16 }}>
//     {data.additionalSections.map((section, idx) => (
//       <View key={idx} style={{ marginTop: idx > 0 ? 12 : 0 }} wrap={false}>
//         <Text style={[TEMPLATE]Styles.mainHeading}>{section.heading.toUpperCase()}</Text>
//         {Array.isArray(section.content) ? (
//           <View>
//             {section.content.map((item, i) => (
//               <Text key={i} style={[TEMPLATE]Styles.bullet}>• {item}</Text>
//             ))}
//           </View>
//         ) : (
//           <Text style={[TEMPLATE]Styles.summary}>{section.content}</Text>
//         )}
//       </View>
//     ))}
//   </View>
// )}
//
// Replace [TEMPLATE] with: modern, professional, creative, or minimal
// This data is already being parsed correctly by resumeModel.js


export async function captureDownloadLead({ name, email, template, resumeName }) {
  const payload = {
    name: (name || '').trim(),
    email: (email || '').trim(),
    template: template || 'modern',
    resume_name: (resumeName || '').trim(),
  };

  const res = await fetch(`${getApiBaseUrl()}/capture_download_lead/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const data = await safeJson(res);
  if (!res.ok) {
    throw new Error(data?.error || data?.message || 'Failed to capture lead');
  }
  return data;
}

